import { ApiClient } from './api-client';
import type { AuthUser, LoginResponse, VerificationStatus } from '../types';

export interface RegisterPayload {
  email: string;
  password: string;
  /** Optional public username (V49). When omitted the backend generates a
   *  neutral one ("user_xxxxxx"). Send it omitted (never "") when the user
   *  leaves the field blank — the backend rejects a malformed non-empty value. */
  username?: string;
  /** Optional in the simplified signup. When omitted (or sent as ""), the
   *  backend stores NULL and the UI falls back to the email's local-part
   *  for greetings via {@code userDisplayName}. */
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export const authService = {
  login: async (email: string, password: string): Promise<LoginResponse> =>
    (await ApiClient.post<LoginResponse>('/auth/login', { email, password })).data,

  /** Whether the backend has Google OAuth credentials configured. Used by
   *  SocialAuthButtons to hide the "Continue with Google" button when the
   *  /start endpoint would 503. */
  isGoogleOAuthEnabled: async (): Promise<boolean> =>
    (await ApiClient.get<{ enabled: boolean }>('/auth/oauth/google/status')).data.enabled,

  register: async (data: RegisterPayload): Promise<LoginResponse> =>
    (await ApiClient.post<LoginResponse>('/auth/register', data)).data,

  logout: async (): Promise<void> => {
    await ApiClient.post('/auth/logout');
  },

  /**
   * Exchange a refresh token for a fresh access+refresh pair AND the
   * up-to-date {@link AuthUser}. The user payload lets the AuthProvider
   * rehydrate localStorage on every page load so role / permission changes
   * (e.g. CUSTOMER → PROVIDER after the become-provider flow) are picked
   * up without a manual logout.
   */
  refresh: async (refreshToken: string) =>
    (
      await ApiClient.post<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>('/auth/refresh', { refreshToken })
    ).data,

  forgotPassword: async (email: string): Promise<void> => {
    await ApiClient.post('/auth/forgot-password', { email });
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await ApiClient.post('/auth/reset-password', { token, newPassword });
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await ApiClient.post('/auth/change-password', { currentPassword, newPassword });
  },

  // ─── Verification (step 1: email, step 2: phone) ──────────────────────
  getVerificationStatus: async (): Promise<VerificationStatus> =>
    (await ApiClient.get<VerificationStatus>('/auth/verification-status')).data,

  verifyEmail: async (code: string): Promise<void> => {
    await ApiClient.post('/auth/verify-email', { code });
  },

  resendEmailVerification: async (): Promise<void> => {
    await ApiClient.post('/auth/resend-email-verification');
  },

  requestPhoneVerification: async (): Promise<void> => {
    await ApiClient.post('/auth/request-phone-verification');
  },

  verifyPhone: async (code: string): Promise<void> => {
    await ApiClient.post('/auth/verify-phone', { code });
  },
};
